import { type AlertProps } from '@mui/material'
import { makeAutoObservable, runInAction } from 'mobx'
import { endOfMonth } from 'date-fns/endOfMonth'
import { isAfter } from 'date-fns/isAfter'
import { isBefore } from 'date-fns/isBefore'
import { isSameDay } from 'date-fns/isSameDay'
import { isWithinInterval } from 'date-fns/isWithinInterval'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfToday } from 'date-fns/startOfToday'
import { subDays } from 'date-fns/subDays'

import { RepeatingTransactionsApi } from '../api'
import {
  type Recurrence,
  type RecurringCopy,
  type RepeatingTransaction,
  type Transaction,
  RecurrenceStatus,
  RecurrenceType,
  type DateRange,
} from '../types'
import {
  augmentRepeatingTransactions,
  buildAllRecurrences,
} from '../utils/RecurringTransactionsUtil'

import { GlobalStore } from './GlobalStore'

const currentMonthFilter = (date: DateRange, recurrence: Recurrence) =>
  isWithinInterval(recurrence.expectedDate, date)

const recurrenceTotalReducer = (total: number, recurrence: Recurrence) => total + recurrence.amount

interface RecurringAlertProps {
  message: string
  severity: AlertProps['severity']
}

export class RecurringTransactionsStore {
  globalStore: GlobalStore
  api = new RepeatingTransactionsApi('/', '')

  repeatingTransactions: RepeatingTransaction[] = []
  isDataLoaded: boolean = false

  selectedDay: Date | undefined
  dateRange: DateRange = { start: startOfMonth(startOfToday()), end: endOfMonth(startOfToday()) }
  selectedRepeatingTransactionGuid: string | null = null
  selectedRecurrence: Recurrence | null = null

  alert: RecurringAlertProps = { message: '', severity: undefined }

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore
    this.api = new RepeatingTransactionsApi(
      globalStore.endpoint,
      globalStore.sessionToken,
      globalStore.onError,
    )

    // The *ForRange helpers are parameterized computeds: they must stay plain
    // methods (not actions) so observable reads inside them remain tracked when
    // invoked from an observer/derivation. makeAutoObservable would otherwise
    // annotate them as actions, which do not track reads.
    makeAutoObservable(this, {
      recurrencesForRange: false,
      upcomingExpensesForRange: false,
      upcomingIncomeForRange: false,
      incomeForRange: false,
      nextIncomeForRange: false,
    })
  }

  get copy(): RecurringCopy {
    return this.globalStore.copyStore.copy.recurring
  }

  get detailedRepeatingTransactions(): RepeatingTransaction[] {
    return augmentRepeatingTransactions(
      this.globalStore.accountStore.visibleAccounts,
      this.repeatingTransactions,
      this.globalStore.transactionStore.transactions,
    )
  }

  /**
   * Returns all recurrences built from repeating transactions and filtered by
   * accounts, scoped to the supplied date range. This is the range-parameterized
   * core used by both the shared getters (which pass this.dateRange) and by
   * consumers such as Cash Flow that need their own independent window without
   * mutating the shared dateRange.
   */
  recurrencesForRange = (dateRange: DateRange): Recurrence[] => {
    const accounts = this.globalStore.accountStore.visibleAccounts

    return buildAllRecurrences(this.detailedRepeatingTransactions, dateRange)
      .filter((r) => accounts.some((a) => a.guid === r.accountGuid && !a.is_excluded_from_accounts))
      .filter((r) => isWithinInterval(r.expectedDate, dateRange))
  }

  /**
   * Returns the upcoming expense recurrences scoped to the supplied date range.
   */
  upcomingExpensesForRange = (dateRange: DateRange): Recurrence[] => {
    return this.recurrencesForRange(dateRange).filter(
      (recurrence: Recurrence) =>
        recurrence.type === RecurrenceType.Expense &&
        recurrence.status === RecurrenceStatus.Upcoming,
    )
  }

  /**
   * Returns the income recurrences scoped to the supplied date range.
   */
  incomeForRange = (dateRange: DateRange): Recurrence[] => {
    return this.recurrencesForRange(dateRange).filter(
      (recurrence: Recurrence) => recurrence.type === RecurrenceType.Income,
    )
  }

  /**
   * Returns the upcoming income recurrences scoped to the supplied date range.
   */
  upcomingIncomeForRange = (dateRange: DateRange): Recurrence[] => {
    return this.incomeForRange(dateRange).filter(
      (recurrence: Recurrence) => recurrence.status === RecurrenceStatus.Upcoming,
    )
  }

  /**
   * Returns the next upcoming income recurrence scoped to the supplied range.
   */
  nextIncomeForRange = (dateRange: DateRange): Recurrence | undefined => {
    return this.upcomingIncomeForRange(dateRange).find((r) =>
      isAfter(r.expectedDate, startOfToday()),
    )
  }

  /**
   * Returns all recurrences built from repeating transactions and filtered by accounts.
   */
  get recurrences(): Recurrence[] {
    return this.recurrencesForRange(this.dateRange)
  }

  /**
   * Returns all recurrences that have occurred in the past, including those that are paid or missed
   */
  get pastRecurrences(): Recurrence[] {
    return this.recurrences.filter(
      (recurrence: Recurrence) =>
        recurrence.status === RecurrenceStatus.Paid ||
        recurrence.status === RecurrenceStatus.Missed,
    )
  }

  /**
   * Returns all recurrences that are expected to occur through the end of the month
   * or should have occurred in the last 3 days.
   */
  get upcomingRecurrences(): Recurrence[] {
    return this.recurrences.filter(
      (r) =>
        isAfter(r.expectedDate, subDays(startOfToday(), 3)) &&
        isBefore(r.expectedDate, this.dateRange.end) &&
        r.status === RecurrenceStatus.Upcoming,
    )
  }

  /**
   * Returns all expense recurrences.
   */
  get expenses(): Recurrence[] {
    return this.recurrences.filter(
      (recurrence: Recurrence) => recurrence.type === RecurrenceType.Expense,
    )
  }

  /**
   * Returns the upcoming expense recurrences.
   */
  get upcomingExpenses(): Recurrence[] {
    return this.expenses.filter(
      (recurrence: Recurrence) => recurrence.status === RecurrenceStatus.Upcoming,
    )
  }

  /**
   * Returns all income recurrences.
   */
  get income(): Recurrence[] {
    return this.recurrences.filter(
      (recurrence: Recurrence) => recurrence.type === RecurrenceType.Income,
    )
  }

  /**
   * Returns the upcoming income recurrences.
   */
  get upcomingIncome(): Recurrence[] {
    return this.income.filter(
      (recurrence: Recurrence) => recurrence.status === RecurrenceStatus.Upcoming,
    )
  }

  get nextIncomeRecurrence() {
    return this.upcomingIncome.find((r) => isAfter(r.expectedDate, startOfToday()))
  }

  /**
   * Returns the expense recurrences for the current month.
   */
  get currentMonthExpenses(): Recurrence[] {
    return this.expenses.filter((recurrence: Recurrence) =>
      currentMonthFilter(this.dateRange, recurrence),
    )
  }

  /**
   * Returns the income recurrences for the current month.
   */
  get currentMonthIncome(): Recurrence[] {
    return this.income.filter((recurrence: Recurrence) =>
      currentMonthFilter(this.dateRange, recurrence),
    )
  }

  /**
   * Calculates the total amount of all expenses for the current month.
   */
  get expenseTotal(): number {
    return this.currentMonthExpenses.reduce(recurrenceTotalReducer, 0)
  }

  /**
   * Calculates the total amount of all paid expenses for the current month.
   */
  get paidExpenseTotal(): number {
    return this.currentMonthExpenses
      .filter((recurrence: Recurrence) => recurrence.status === RecurrenceStatus.Paid)
      .reduce(recurrenceTotalReducer, 0)
  }

  /**
   * Calculates the total amount of all missed expenses for the current month.
   */
  get missedExpenseTotal(): number {
    return this.currentMonthExpenses
      .filter((recurrence: Recurrence) => recurrence.status === RecurrenceStatus.Missed)
      .reduce(recurrenceTotalReducer, 0)
  }

  /**
   * Calculates the total amount of all upcoming expenses for the current month.
   */
  get upcomingExpenseTotal(): number {
    return this.currentMonthExpenses
      .filter((recurrence: Recurrence) => recurrence.status === RecurrenceStatus.Upcoming)
      .reduce(recurrenceTotalReducer, 0)
  }

  /**
   * Calculates the total amount of all income for the current month.
   */
  get incomeTotal(): number {
    return this.currentMonthIncome.reduce(recurrenceTotalReducer, 0)
  }

  /**
   * Calculates the total amount of all paid income for the current month.
   */
  get paidIncomeTotal(): number {
    return this.currentMonthIncome
      .filter((recurrence: Recurrence) => recurrence.status === RecurrenceStatus.Paid)
      .reduce(recurrenceTotalReducer, 0)
  }

  /**
   * Calculates the total amount of all upcoming income for the current month.
   */
  get upcomingIncomeTotal(): number {
    return this.currentMonthIncome
      .filter((recurrence: Recurrence) => recurrence.status === RecurrenceStatus.Upcoming)
      .reduce(recurrenceTotalReducer, 0)
  }

  /**
   * Get the recurrences for the currently selected day.
   */
  get selectedDayRecurrences(): Recurrence[] | undefined {
    return (
      this.selectedDay &&
      this.recurrences.filter(
        (recurrence: Recurrence) =>
          this.selectedDay &&
          isSameDay(recurrence.occurredOnDate || recurrence.expectedDate, this.selectedDay),
      )
    )
  }

  get selectedRepeatingTransaction(): RepeatingTransaction | undefined {
    return this.selectedRepeatingTransactionGuid
      ? this.detailedRepeatingTransactions.find(
          (rt) => rt.guid === this.selectedRepeatingTransactionGuid,
        )
      : undefined
  }

  setSelectedRepeatingTransactionGuid = (guid: string | null) =>
    (this.selectedRepeatingTransactionGuid = guid)

  setSelectedRecurrence = (recurrence: Recurrence | null) => (this.selectedRecurrence = recurrence)

  setRepeatingTransactions = (repeating: RepeatingTransaction[]) => {
    this.repeatingTransactions = repeating
    this.isDataLoaded = true
  }

  /**
   * Set the date range for filtering recurrences.
   * @param dateRange - The date range to set.
   */
  setDateRange = (dateRange: DateRange) => (this.dateRange = dateRange)

  /**
   * Set the currently selected day.
   * @param day - The day to select.
   */
  setSelectedDay = (day: Date) => {
    this.selectedDay = this.selectedDay && isSameDay(day, this.selectedDay) ? undefined : day
  }

  loadRepeatingTransactionData = async () => {
    await Promise.all([
      this.globalStore.accountStore.loadAccountData(),
      this.globalStore.categoryStore.loadCategories(),
      this.globalStore.transactionStore.loadTransactionData(),
    ])
    await this.loadRepeatingTransactions()
  }

  /**
   * Load repeating transactions and augment them with additional data.
   */
  loadRepeatingTransactions = async () => {
    try {
      const repeatingTransactions = await this.api.getRepeatingTransactions()
      this.setRepeatingTransactions(repeatingTransactions)
    } catch (error) {
      console.error('Error occurred while getting repeating transactions:', error)
      this.globalStore.captureException(error, { store: 'RecurringTransactionsStore' })
    }
  }

  /**
   * Add a new repeating transaction and link it to the associated transaction.
   * @param repeatingTransaction - The repeating transaction to add.
   * @param transaction - The transaction associated with the repeating transaction.
   */
  addRepeatingTransaction = async (
    repeatingTransaction: RepeatingTransaction,
    transaction: Transaction,
  ): Promise<boolean> => {
    try {
      const newRepeating = await this.api.addRepeatingTransaction(repeatingTransaction)

      await this.globalStore.transactionStore.updateTransaction({
        ...transaction,
        repeating_transaction_guid: newRepeating.guid,
      })

      runInAction(() => {
        this.repeatingTransactions = [
          ...this.repeatingTransactions,
          ...augmentRepeatingTransactions(
            this.globalStore.accountStore.visibleAccounts,
            [newRepeating],
            this.globalStore.transactionStore.transactions,
          ),
        ]
      })

      this.setAlert(this.copy.add_repeating_transaction_success, 'success')

      return true
    } catch (error) {
      console.error('Error occurred while adding repeating transaction:', error)
      this.globalStore.captureException(error, { store: 'RecurringTransactionsStore' })
      this.setAlert(this.copy.add_repeating_transaction_error, 'error')

      return false
    }
  }

  /**
   * Delete a repeating transaction by its GUID.
   * @param guid - The GUID of the repeating transaction to delete.
   */
  deleteRepeatingTransaction = async (guid: string): Promise<boolean> => {
    try {
      await this.api.deleteRepeatingTransaction(guid)

      runInAction(() => {
        this.repeatingTransactions = [
          ...this.repeatingTransactions.filter((rt) => rt.guid !== guid),
        ]
      })

      this.setAlert(this.copy.remove_repeating_transaction_success, 'success')

      return true
    } catch (error) {
      console.error('Error occurred while deleting repeating transaction:', error)
      this.globalStore.captureException(error, { store: 'RecurringTransactionsStore' })
      this.setAlert(this.copy.remove_repeating_transaction_error, 'error')

      return false
    }
  }

  /**
   * Update an existing repeating transaction.
   * @param transaction - The repeating transaction to update.
   */
  updateRepeatingTransaction = async (transaction: RepeatingTransaction): Promise<boolean> => {
    try {
      const updatedTransaction = await this.api.updateRepeatingTransaction(transaction)

      const index = this.repeatingTransactions.findIndex(
        (rt) => rt.guid === updatedTransaction.guid,
      )
      if (index < 0) return false

      runInAction(() => {
        this.repeatingTransactions = [
          ...this.repeatingTransactions.slice(0, index),
          ...augmentRepeatingTransactions(
            this.globalStore.accountStore.visibleAccounts,
            [updatedTransaction],
            this.globalStore.transactionStore.transactions,
          ),
          ...this.repeatingTransactions.slice(index + 1),
        ]
      })

      this.setAlert(this.copy.update_repeating_transaction_success, 'success')

      return true
    } catch (error) {
      console.error('Error occurred while updating repeating transaction:', error)
      this.globalStore.captureException(error, { store: 'RecurringTransactionsStore' })
      this.setAlert(this.copy.update_repeating_transaction_error, 'error')

      return false
    }
  }

  /**
   * Mark a recurrence as paid by linking a transaction to the repeating transaction.
   * @param repeatingTransactionGuid - The GUID of the repeating transaction.
   * @param transaction - The transaction to link.
   */
  markRecurrenceAsPaid = async (repeatingTransactionGuid: string, transaction: Transaction) => {
    try {
      await this.globalStore.transactionStore.updateTransaction({
        ...transaction,
        repeating_transaction_guid: repeatingTransactionGuid,
      })

      await this.loadRepeatingTransactions()
      this.setSelectedRecurrence(null)
      this.setAlert(this.copy.link_transaction_success, 'success')
    } catch (error) {
      console.error('Error occurred while marking recurrence as paid:', error)
      this.globalStore.captureException(error, { store: 'RecurringTransactionsStore' })
      this.setAlert(this.copy.link_transaction_error, 'error')
    }
  }

  /**
   * Mark a recurrence as unpaid by unlinking the associated transaction from the repeating transaction.
   * @param transaction - The transaction to unlink.
   */
  markRecurrenceAsUnpaid = async (transaction: Transaction) => {
    try {
      await this.globalStore.transactionStore.updateTransaction({
        ...transaction,
        repeating_transaction_guid: null,
      })

      await this.loadRepeatingTransactions()
      this.setSelectedRecurrence(null)
      this.setAlert(this.copy.unlink_transaction_success, 'success')
    } catch (error) {
      console.error('Error occurred while marking recurrence as unpaid:', error)
      this.globalStore.captureException(error, { store: 'RecurringTransactionsStore' })
      this.setAlert(this.copy.unlink_transaction_error, 'error')
    }
  }

  setAlert = (message: string, severity: RecurringAlertProps['severity'] = undefined) =>
    (this.alert = { message, severity })
}
